import React from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  IconographyThemes,
  Loader,
  LoaderSizes,
  Pagination,
  TextArea,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { useNavigate } from "react-router";
import DeleteModal from "../../../../../../common/components/DeleteModal";
import {
  addNoteSagaAction,
  deleteNoteSagaAction,
  getNotesListSagaAction,
  updateNoteSagaAction,
} from "../../../../../../redux/saga/notes/saga-actions";
import "./Notes.scss";
import moment from "moment";
import { NoteType } from "../../../../../../redux/slices/notes/Types";
import { usePartnerTabPermission } from "../../../../../../contexts/PartnerPermissionContext";
import { useSessionCanEditModule } from "../../../../../../hooks/useSessionPermissions";
import { PARTNER_MANAGEMENT_PERMISSION_KEYS as PM_KEYS } from "../../../../../../utils/permissions/partnerManagementPermissionKeys";

const Notes = () => {
  const [noteText, setNoteText] = React.useState<string>("");
  const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const { notesList, status } = useAppSelector((state) => state.notes);
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { readOnly: tabReadOnly } = usePartnerTabPermission();
  const canEditNotes = useSessionCanEditModule(PM_KEYS.PARTNER_NOTES);
  const notesReadOnly = tabReadOnly || !canEditNotes;

  React.useEffect(() => {
    if (
      notesList.length === 0 ||
      notesList[0]?.partner_id !== partnerDetails.ID
    )
      dispatch(getNotesListSagaAction(partnerDetails.ID));
  }, []);

  const pageSize = 7;
  const totalRecords = notesList.length;

  const handlePageChange = (clickedPageNumber: number | null) => {
    setCurrentPageNumber(clickedPageNumber);
  };

  const paginatedData = notesList.slice(
    (currentPageNumber - 1) * pageSize,
    currentPageNumber * pageSize
  );

  const onAddClick = () => {
    dispatch(
      addNoteSagaAction({
        ID: 0,
        partner_id: partnerDetails.ID,
        user_email: loggedUserDetail.email,
        note: noteText.trim(),
        created_at: "",
        updated_at: "",
      })
    );
    setNoteText("");
  };
  return (
    <div className="notes-container">
      <TextArea
        label="Add a note"
        id="noteText"
        placeholder=""
        name="noteText"
        maxLength={1000}
        theme="dark"
        value={noteText}
        onChange={(e: any) => setNoteText(e.currentTarget.value)}
        disabled={notesReadOnly}
      />
      <div className="button-group">
        <Button
          variant="primary"
          label="Add"
          onClick={() => {
            onAddClick();
          }}
          disabled={notesReadOnly || status.addNoteLoading || !noteText.trim()}
        />
        <div
          className="form-link"
          onClick={() =>
            navigate(`/hid-admin/sales-partner-note-form/${partnerDetails.ID}`)
          }
        >
          {"Struggling to close service menu deals? Share us why"}
        </div>
      </div>
      {status.notesListLoading ? (
        <div className="list-loading">
          <Loader variant={LoaderSizes.large} />
        </div>
      ) : (
        <>
          <div className="table-container">
            <div className="table-header">
              <div className="col-1">Timestamp</div>
              <div className="col-2">Note</div>
              <div className="col-3">Submitted By</div>
            </div>

            {paginatedData.length > 0 ? (
              paginatedData.map((note, index) => (
                <NoteRow note={note} index={index} key={note.ID} readOnly={notesReadOnly} />
              ))
            ) : (
              <div className="no-data">No data available for display.</div>
            )}
          </div>
          <Pagination
            currentPageNumber={currentPageNumber}
            totalRecords={totalRecords}
            pageSize={pageSize}
            border={true}
            onPageNumberClick={handlePageChange}
          />
        </>
      )}
    </div>
  );
};

const NoteRow: React.FC<{ note: NoteType; index: number; readOnly?: boolean }> = ({
  note,
  index,
  readOnly = false,
}) => {
  const [editNote, setEditNote] = React.useState<boolean>(false);
  const [noteText, setNoteText] = React.useState<string>(note.note);
  const [deleteOpen, setDeleteOpen] = React.useState<boolean>(false);
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const { status } = useAppSelector((state) => state.notes);
  const dispatch = useAppDispatch();
  const canEditNotes = useSessionCanEditModule(PM_KEYS.PARTNER_NOTES);
  const canManageNote = canEditNotes || loggedUserDetail?.email === note.user_email;
  
  const maxLength = 190;
  const shouldTruncate = note.note.length > maxLength;
  const displayText = isExpanded || !shouldTruncate 
    ? note.note 
    : `${note.note.substring(0, maxLength)}...`;
  const onSaveClick = () => {
    dispatch(updateNoteSagaAction({ ...note, note: noteText.trim() }));
    setEditNote(false);
  };

  const onDeleteButtonClick = () => {
    dispatch(
      deleteNoteSagaAction({
        noteID: note.ID,
        setModalOpen: setDeleteOpen,
      })
    );
  };
  return (
    <div
      className="table-row"
      key={note.ID}
      style={index === 0 ? { border: "none" } : {}}
    >
      <div className="col-1">
        <span>{moment(note.created_at).format("DD MMM yyyy")}</span>
      </div>
      <div className="col-2">
        {editNote ? (
          <TextArea
            label=""
            id="noteText"
            placeholder=""
            name="noteText"
            maxLength={1000}
            theme="dark"
            value={noteText}
            isDisplayCharCount={false}
            onChange={(e: any) => setNoteText(e.currentTarget.value)}
          />
        ) : (
          <>
            <span>{displayText}</span>
            {shouldTruncate && (
              <button
                className="read-more-link"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? "Read less" : "Read more"}
              </button>
            )}
          </>
        )}
      </div>
      <div className="col-3">{note.user_email}</div>
      {canManageNote && (
        <>
          {editNote ? (
            <div className="action-buttons" style={{ flexDirection: "column" }}>
              <Button
                variant="primary"
                label="Save"
                onClick={() => {
                  onSaveClick();
                }}
                disabled={
                  readOnly || status.updateNoteLoading || !noteText.trim()
                }
              />
              <Button
                variant="secondary"
                label="Cancel"
                onClick={() => {
                  setEditNote(false);
                  setNoteText(note.note);
                }}
                disabled={readOnly}
              />
            </div>
          ) : (
            <div className="action-buttons">
              <Button
                variant="secondary"
                icon={
                  <Iconography
                    icon="pen"
                    size={IconographySizes.Medium}
                    theme={IconographyThemes.Info}
                  />
                }
                onClick={() => {
                  setEditNote(true);
                }}
                disabled={readOnly}
              />
              <Button
                className="delete-button"
                variant="secondary"
                icon={
                  <Iconography
                    icon="trashCan"
                    size={IconographySizes.Medium}
                    theme={IconographyThemes.Error}
                  />
                }
                onClick={() => {
                  setDeleteOpen(true);
                }}
                disabled={readOnly}
              />
            </div>
          )}
        </>
      )}
      {deleteOpen && (
        <DeleteModal
          setDeleteOpen={setDeleteOpen}
          onDeleteButtonClick={onDeleteButtonClick}
          deleteButtonLoading={status.deleteNoteLoading}
          optionalHeader="Delete Note"
          width={320}
          message="This will delete the note permanently"
        />
      )}
    </div>
  );
};

export default Notes;
